import { createElement, useCallback, useEffect, useRef, useState } from "react";
import {
  Image,
  Platform,
  StyleSheet,
  Text,
  View,
  useWindowDimensions,
} from "react-native";
import { useLocalSearchParams, useRouter } from "expo-router";
import * as Linking from "expo-linking";
import QRCode from "react-native-qrcode-svg";
import { CalendarDays, RefreshCw } from "lucide-react-native";
import {
  registerAction,
  registerError,
  type RegisterEventCard,
  type RegisterPersonCard,
  type RegisterService,
  type RegisterWorkspace,
  type WebsiteObituary,
} from "../lib/central-register-workspace";
import { generatePublicRegisterUrl } from "../lib/central-event-guest-utils";
import * as Clipboard from "../lib/clipboard";
import { useCentralCapability } from "../hooks/useCentralCapability";
import { central, spacing } from "../theme/tokens";
import { Badge, Button, EmptyState, Field, LoadingState, Panel } from "./ui";
import { CentralAttendanceOperations } from "./CentralAttendanceOperations";

export function CentralEventWorkspace() {
  const params = useLocalSearchParams<{
    personId?: string;
    eventId?: string;
    new?: string;
    newEvent?: string;
  }>();
  return params.personId ? (
    <EventDetail
      key={`${params.personId}:${params.eventId ?? ""}:${params.newEvent ?? ""}`}
      personId={String(params.personId)}
      eventId={params.eventId ? String(params.eventId) : undefined}
      startAdding={params.newEvent === "1"}
    />
  ) : (
    <EventIndex startNew={params.new === "1"} />
  );
}
export function EventIndex({ startNew, peopleMode = false }: { startNew: boolean; peopleMode?: boolean }) {
  const router = useRouter();
  const { can } = useCentralCapability();
  const [events, setEvents] = useState<RegisterEventCard[]>([]);
  const [people, setPeople] = useState<RegisterPersonCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("All Events");
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState(startNew ? "new" : "list");
  const [name, setName] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [website, setWebsite] = useState<WebsiteObituary[]>([]);
  const [websiteQuery, setWebsiteQuery] = useState("");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const result = await registerAction<{ events: RegisterEventCard[]; people: RegisterPersonCard[] }>("list");
      setEvents(result.events);
      setPeople(result.people);
    } catch (e) {
      setError(registerError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const open = (personId: string, eventId?: string) =>
    router.push(
      `/events?personId=${encodeURIComponent(personId)}${eventId ? `&eventId=${encodeURIComponent(eventId)}` : ""}` as never,
    );
  const create = async (url?: string) => {
    if (busy) return;
    setBusy(true);
    setError("");
    try {
      const result = await registerAction<{ personId: string }>(
        url ? "import" : "create",
        url ? { sourceUrl: url } : { fullName: name },
      );
      if (peopleMode) router.push(`/workspace/case-overview?caseId=${encodeURIComponent(result.personId)}` as never);
      else open(result.personId);
    } catch (e) {
      setError(registerError(e));
    } finally {
      setBusy(false);
    }
  };
  const discover = async (next = 1) => {
    setBusy(true);
    setError("");
    try {
      const result = await registerAction<{
        entries: WebsiteObituary[];
        hasMore: boolean;
      }>("discover", { page: next, query: websiteQuery });
      setWebsite(result.entries);
      setHasMore(result.hasMore);
      setPage(next);
    } catch (e) {
      setError(registerError(e));
    } finally {
      setBusy(false);
    }
  };
  const now = Date.now();
  const normalized = query.trim().toLowerCase();
  const visible = events
    .filter((event) =>
      [event.lovedOneName, event.title, event.venue]
        .join(" ")
        .toLowerCase()
        .includes(normalized),
    )
    .filter((event) =>
      filter === "All Events" || filter === "Drafts"
        ? filter === "All Events" || event.registerStatusLabel === "Draft"
        : filter === "Past"
          ? event.registerStatusLabel !== "Draft" &&
            event.registerStatusLabel !== "Open" &&
            Date.parse(event.endAt || event.startAt) < now
          : event.registerStatusLabel === "Open" ||
            (event.registerStatusLabel !== "Draft" &&
              Date.parse(event.endAt || event.startAt) >= now),
    );
  return (
    <View style={styles.page}>
      <View style={styles.headingRow}>
        <View style={styles.flex}>
          <Text accessibilityRole="header" style={styles.heading}>
            {peopleMode ? "Cases" : "Events"}
          </Text>
          <Text style={styles.body}>
            {peopleMode ? "Find a decedent, edit their profile, and manage all their events." : "Every service and its guest register, across all decedents."}
          </Text>
        </View>
        <View style={styles.actions}>
          <Button
            label="Sync From Website"
            variant="secondary"
            disabled={!can("mutations") || busy}
            onPress={() => {
              setMode("website");
              void discover();
            }}
          />
          <Button
            label={peopleMode ? "New Profile" : "New Event"}
            disabled={!can("mutations") || busy}
            onPress={() => {
              setMode("new");
              setError("");
            }}
          />
        </View>
      </View>
      {error ? <Alert message={error} /> : null}
      {mode !== "list" ? (
        <Panel style={styles.panel}>
          <View style={styles.headingRow}>
            <Text accessibilityRole="header" style={styles.title}>
              {mode === "website" ? "Import From Website" : peopleMode ? "New Profile" : "New Event"}
            </Text>
            <Button
              label="Cancel"
              variant="link"
              disabled={busy}
              onPress={() => setMode("list")}
            />
          </View>
          {mode === "new" ? (
            <>
              <Text style={styles.body}>
                Start with the person being remembered. Service details come
                next.
              </Text>
              <Field
                label="Person’s Full Name"
                required
                value={name}
                onChangeText={setName}
              />
              <Button
                label={peopleMode ? "Create Profile" : "Continue to Event Setup"}
                disabled={busy || name.trim().length < 2}
                loading={busy}
                onPress={() => void create()}
              />
            </>
          ) : (
            <>
              <Text style={styles.body}>
                Choose an obituary to import its service details and preview the
                guest sign-in page.
              </Text>
              <Field
                label="Search Website Obituaries"
                value={websiteQuery}
                onChangeText={setWebsiteQuery}
                placeholder="Name"
              />
              <Button
                label="Search Website"
                variant="secondary"
                disabled={busy}
                onPress={() => void discover()}
              />
              {busy ? <LoadingState label="Reading Website" /> : null}
              {website.map((item) => (
                <View key={item.sourceUrl} style={styles.websiteRow}>
                  {item.photoUrl ? (
                    <Image
                      source={{ uri: item.photoUrl }}
                      style={styles.smallPortrait}
                    />
                  ) : null}
                  <View style={styles.flex}>
                    <Text style={styles.title}>{item.fullName}</Text>
                    <Text style={styles.caption}>{item.deathDate}</Text>
                  </View>
                  <Button
                    label="Import"
                    disabled={busy}
                    onPress={() => void create(item.sourceUrl)}
                  />
                </View>
              ))}
              {!busy && !website.length ? (
                <Text style={styles.body}>
                  No website results to show. Search by name or paste an
                  obituary link below.
                </Text>
              ) : null}
              <View style={styles.actions}>
                <Button
                  label="Previous"
                  variant="link"
                  disabled={busy || page === 1}
                  onPress={() => void discover(page - 1)}
                />
                <Button
                  label="Next"
                  variant="link"
                  disabled={busy || !hasMore}
                  onPress={() => void discover(page + 1)}
                />
              </View>
              <View style={styles.divider} />
              <Field
                label="Or Paste an Obituary Link"
                value={sourceUrl}
                onChangeText={setSourceUrl}
                placeholder="Paste the person’s obituary URL"
                autoCapitalize="none"
              />
              <Button
                label="Import From Link"
                variant="secondary"
                disabled={busy || !sourceUrl.trim()}
                onPress={() => void create(sourceUrl)}
              />
            </>
          )}
        </Panel>
      ) : null}
      <Field
        label={peopleMode ? "Find a Decedent" : "Find an Event"}
        value={query}
        onChangeText={setQuery}
        placeholder={peopleMode ? "Search by name" : "Search by person, event, or venue"}
      />
      <View style={styles.tabs}>
        {(peopleMode ? [] : ["Upcoming", "Drafts", "Past", "All Events"]).map((value) => (
          <Button
            key={value}
            compact
            label={value}
            variant={filter === value ? "primary" : "secondary"}
            onPress={() => setFilter(value)}
          />
        ))}
        <Button
          compact
          label="Refresh"
          variant="link"
          disabled={loading}
          onPress={() => void refresh()}
        />
      </View>
      {loading ? (
        <LoadingState label={peopleMode ? "Loading Cases" : "Loading Events"} />
      ) : peopleMode ? (
        <>
          {people.filter(person => person.fullName.toLowerCase().includes(normalized)).map(person => (
            <Panel key={person.personId} style={styles.eventCard}>
              <View style={styles.headingRow}>
                {person.photoUrl ? <Image source={{ uri: person.photoUrl }} accessibilityLabel={`Portrait of ${person.fullName}`} style={styles.portrait} /> : null}
                <View style={styles.flex}>
                  <Text accessibilityRole="header" style={styles.eventName}>{person.fullName}</Text>
                  <Text style={styles.body}>{[person.birthDate, person.deathDate].filter(Boolean).join(" — ")}</Text>
                  <Text style={styles.caption}>{person.eventCount} {person.eventCount === 1 ? "Event" : "Events"}</Text>
                </View>
                <Button label="View Profile & Events" variant="secondary" onPress={() => router.push(`/workspace/case-overview?caseId=${encodeURIComponent(person.personId)}` as never)} />
              </View>
            </Panel>
          ))}
          {!people.some(person => person.fullName.toLowerCase().includes(normalized)) ? <EmptyState title={query ? "No Matching Decedents" : "Add Your First Profile"} description="Search by name, import an obituary from the website, or create a profile." /> : null}
        </>
      ) : !visible.length ? (
        <EmptyState
          title={
            events.length
              ? "No Events in This View"
              : "Prepare Your First Event"
          }
          description={
            events.length
              ? "Try Drafts, All Events, or another search."
              : "Import from the funeral home website or create an event to prepare its guest register."
          }
        />
      ) : (
        visible.map((event) => (
          <Panel key={`${event.personId}:${event.id}`} style={styles.eventCard}>
            <View style={styles.headingRow}>
              {event.photoUrl ? (
                <Image
                  source={{ uri: event.photoUrl }}
                  style={styles.portrait}
                />
              ) : (
                <View style={styles.portraitPlaceholder}>
                  <CalendarDays size={28} color={central.text.muted} />
                </View>
              )}
              <View style={styles.flex}>
                <Text style={styles.caption}>{event.lovedOneName}</Text>
                <Text accessibilityRole="header" style={styles.eventName}>
                  {event.title}
                </Text>
                <Text style={styles.body}>
                  {event.localStart
                    ? `${event.localStart.replace("T", " · ")} · ${event.timeZone}`
                    : "Date Not Listed on Website"}
                </Text>
                {event.venue ? (
                  <Text style={styles.body}>{event.venue}</Text>
                ) : null}
              </View>
              <Badge
                label={event.registerStatusLabel}
                tone={event.registerStatusLabel === "Open" ? "forest" : "cedar"}
              />
            </View>
            <Button
              label={
                event.registerStatusLabel === "Draft"
                  ? "Prepare Event"
                  : "Open Event"
              }
              variant="secondary"
              onPress={() => open(event.personId, event.id)}
            />
          </Panel>
        ))
      )}
    </View>
  );
}
function EventDetail({
  personId,
  eventId,
  startAdding = false,
}: {
  personId: string;
  eventId?: string;
  startAdding?: boolean;
}) {
  const router = useRouter();
  const { can } = useCentralCapability();
  const { width } = useWindowDimensions();
  const [data, setData] = useState<RegisterWorkspace | null>(null);
  const [draft, setDraft] = useState<RegisterService | null>(null);
  const [tab, setTab] = useState("Overview");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [obituary, setObituary] = useState("");
  const [photo, setPhoto] = useState("");
  const [sourceUrl, setSourceUrl] = useState("");
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const generation = useRef(0);
  const initialLoad = useRef(true);
  const refresh = useCallback(
    async (selectId?: string) => {
      const current = ++generation.current;
      const result = await registerAction<RegisterWorkspace>("get", {
        personId,
      });
      if (current !== generation.current) return;
      setData(result);
      const first = initialLoad.current;
      if (first) {
        setTab(result.websiteInfo ? "Register" : "Overview");
        initialLoad.current = false;
      }
      setDraft(
        result.events.find((item) => item.id === (selectId || eventId)) ??
          result.events[0] ??
          null,
      );
      setObituary(result.obituary);
      setPhoto(result.photoUrl);
      setSourceUrl(result.websiteInfo?.sourceUrl ?? "");
      setDirty(false);
      setAdding(false);
      if (first && startAdding) {
        setDraft(newRegisterService()); setAdding(true); setDirty(true); setTab("Overview");
      }
    },
    [personId, eventId, startAdding],
  );
  useEffect(() => {
    void refresh().catch((e) => setError(registerError(e)));
    return () => {
      generation.current++;
    };
  }, [refresh]);
  const mutate = async (action: string) => {
    if (!data || !draft || busy) return;
    setBusy(true);
    setError("");
    setNotice("");
    try {
      await registerAction(action, {
        personId,
        eventId: draft.id,
        expectedVersion: data.version,
        event: draft,
        obituary,
        photoUrl: photo,
        sourceUrl,
      });
      await refresh(draft.id);
      setNotice(
        action === "sync"
          ? "Latest website details are ready to review below. Saved event details and guests have been kept."
          : action === "open"
            ? "Guest signing is open. Close it when you are finished."
            : action === "close"
              ? "Register closed. Guest records remain available."
              : "Event details saved.",
      );
    } catch (e) {
      setError(registerError(e));
    } finally {
      setBusy(false);
    }
  };
  if (!data || !draft)
    return (
      <View style={styles.page}>
        {error ? (
          <>
            <Alert message={error} />
            <Button
              label="Retry"
              onPress={() =>
                void refresh().catch((e) => setError(registerError(e)))
              }
            />
          </>
        ) : (
          <LoadingState label="Loading Event" />
        )}
      </View>
    );
  const readOnly = data.readOnly || !can("mutations");
  const open = draft.registerStatusLabel === "Open";
  const saved = !dirty && !adding;
  const publicOrigin =
    typeof window !== "undefined" &&
    window.location.hostname.includes("--register-central-test-")
      ? "https://wehonorhome--register-phone-test-225hglfw.web.app"
      : typeof window !== "undefined" &&
          window.location.hostname.endsWith(".localhost")
        ? window.location.origin
        : "https://wehonor.app";
  const url = data.slug
    ? generatePublicRegisterUrl(
        data.funeralHomeId,
        data.slug,
        draft.id,
        publicOrigin,
      )
    : "";
  const change = (key: string, value: string) => {
    setDraft({ ...draft, [key]: value });
    setDirty(true);
  };
  const add = () => {
    setDraft(newRegisterService());
    setAdding(true);
    setDirty(true);
    setTab("Overview");
  };
  return (
    <View style={styles.page}>
      <Button
        label="Back to Events"
        variant="link"
        disabled={busy || dirty}
        onPress={() => router.replace("/events" as never)}
      />
      <Button label="View Profile & All Events" variant="link" disabled={busy || dirty} onPress={() => router.push(`/workspace/case-overview?caseId=${encodeURIComponent(personId)}` as never)} />
      <View style={styles.headingRow}>
        {photo ? (
          <Image source={{ uri: photo }} style={styles.portrait} />
        ) : null}
        <View style={styles.flex}>
          <Text style={styles.caption}>{draft.title}</Text>
          <Text accessibilityRole="header" style={styles.heading}>
            {data.fullName}
          </Text>
          <Text style={styles.body}>
            {[data.person.birthDate, data.person.deathDate]
              .filter(Boolean)
              .join(" — ")}
          </Text>
        </View>
        <Badge
          label={draft.registerStatusLabel}
          tone={open ? "forest" : "cedar"}
        />
      </View>
      {error ? <Alert message={error} /> : null}
      {notice ? (
        <Text accessibilityLiveRegion="polite" style={styles.notice}>
          {notice}
        </Text>
      ) : null}
      {data.events.length > 1 || adding ? (
        <View style={styles.tabs}>
          {data.events.map((event) => (
            <Button
              key={event.id}
              compact
              label={event.title}
              disabled={busy || dirty}
              variant={event.id === draft.id ? "primary" : "secondary"}
              onPress={() => {
                setDraft(event);
                setAdding(false);
                setTab("Overview");
              }}
            />
          ))}
        </View>
      ) : null}
      <View style={styles.tabs}>
        {["Overview", "Register", "Guests & Export"].map((value) => (
          <Button
            key={value}
            label={value}
            variant={tab === value ? "primary" : "secondary"}
            disabled={busy || (adding && value !== "Overview")}
            onPress={() => setTab(value)}
          />
        ))}
      </View>
      {tab === "Overview" ? (
        <>
          <Panel style={styles.panel}>
            <Text accessibilityRole="header" style={styles.title}>
              Service Details
            </Text>
            <Text style={styles.body}>
              Details are filled from the obituary. You can edit them if needed.
              Guest signing stays open until you close it.
            </Text>
            <Field
              label="Event Title"
              required
              value={draft.title}
              onChangeText={(v) => change("title", v)}
              editable={!readOnly && !open && !busy}
            />
            <View style={[styles.columns, width < 700 && styles.stacked]}>
              <DateField
                label="Service Starts"
                value={draft.localStart}
                onChange={(v) => change("localStart", v)}
                disabled={readOnly || open || busy}
              />
              <DateField
                label="Service Ends"
                value={draft.localEnd}
                onChange={(v) => change("localEnd", v)}
                disabled={readOnly || open || busy}
              />
            </View>
            <Field
              label="Time Zone"
              value={draft.timeZone}
              placeholder="America/New_York"
              onChangeText={(v) => change("timeZone", v)}
              editable={!readOnly && !open && !busy}
              autoCapitalize="none"
            />
            <Field
              label="Venue"
              value={draft.venue}
              onChangeText={(v) => change("venue", v)}
              editable={!readOnly && !open && !busy}
            />
            <Field
              label="Address"
              value={draft.address}
              onChangeText={(v) => change("address", v)}
              editable={!readOnly && !open && !busy}
            />
            {draft.sourceStart ? (
              <Text style={styles.sourceText}>
                Website service time: {draft.sourceStart}
                {draft.sourceEnd ? ` – ${draft.sourceEnd}` : ""}. Imported from
                the obituary.
              </Text>
            ) : null}
            {open ? (
              <Text style={styles.body}>
                Close the register before editing event details.
              </Text>
            ) : null}
            <Button
              label="Discard Changes"
              variant="link"
              disabled={busy || !dirty}
              onPress={() =>
                void refresh().catch((e) => setError(registerError(e)))
              }
            />
            <Button
              label="Save Event"
              disabled={readOnly || open || busy || !dirty}
              loading={busy}
              onPress={() => void mutate(adding ? "add" : "save")}
            />
            {!adding ? (
              <Button
                label="Add Another Event for This Person"
                variant="link"
                disabled={readOnly || busy || dirty}
                onPress={add}
              />
            ) : null}
          </Panel>
          <Panel style={styles.panel}>
            <View style={styles.headingRow}>
              <RefreshCw size={20} color={central.action.primary} />
              <Text accessibilityRole="header" style={styles.title}>
                Website Sync
              </Text>
            </View>
            <Text style={styles.body}>
              Pull the latest details into a review snapshot. Sync preserves
              your event setup and guest list.
            </Text>
            <Field
              label="Obituary Link"
              value={sourceUrl}
              onChangeText={setSourceUrl}
              editable={!readOnly && !busy}
              autoCapitalize="none"
            />
            <Button
              label="Sync From Website"
              variant="secondary"
              disabled={readOnly || busy || dirty || !sourceUrl.trim()}
              onPress={() => void mutate("sync")}
            />
            {data.websiteInfo ? (
              <>
                <Text style={styles.caption}>
                  Last synced:{" "}
                  {new Date(data.websiteInfo.syncedAt).toLocaleString()}
                </Text>
                <Text style={styles.title}>{data.websiteInfo.fullName}</Text>
                <Text style={styles.body}>{data.websiteInfo.obituary}</Text>
                {data.websiteInfo.services.map((service, index) => (
                  <Text key={index} style={styles.sourceText}>
                    {service.name} · {service.startDate} · {service.venue}
                  </Text>
                ))}
                <Button
                  label="Use Website Remembrance & Portrait"
                  variant="secondary"
                  disabled={readOnly || busy || open}
                  onPress={() => {
                    setObituary(data.websiteInfo!.obituary.slice(0, 12000));
                    setPhoto(data.websiteInfo!.photoUrl);
                    setDirty(true);
                    setTab("Register");
                  }}
                />
              </>
            ) : null}
          </Panel>
        </>
      ) : tab === "Register" ? (
        <>
          <Panel style={styles.panel}>
            <Text accessibilityRole="header" style={styles.title}>
              Personalize the Welcome
            </Text>
            <Text style={styles.body}>
              Portrait and remembrance are shared by this person’s events.
              Changes go public when a register is opened.
            </Text>
            <Field
              label="Portrait Link"
              value={photo}
              onChangeText={(v) => {
                setPhoto(v);
                setDirty(true);
              }}
              editable={!readOnly && !open && !busy}
              autoCapitalize="none"
            />
            <Field
              label="Remembrance"
              multiline
              value={obituary}
              onChangeText={(v) => {
                setObituary(v);
                setDirty(true);
              }}
              editable={!readOnly && !open && !busy}
            />
            <Button
              label="Save Register Details"
              disabled={readOnly || open || busy || !dirty}
              onPress={() => void mutate("save")}
            />
          </Panel>
          <Panel style={styles.preview}>
            {photo ? (
              <Image source={{ uri: photo }} style={styles.previewPhoto} />
            ) : null}
            <Text style={styles.caption}>IN LOVING MEMORY</Text>
            <Text style={styles.eventName}>{data.fullName}</Text>
            <Text style={styles.body}>
              {obituary
                ? obituary.slice(0, 240)
                : "Please share your presence in the guest register."}
            </Text>
            <Text style={styles.caption}>{draft.title}</Text>
            <Text style={styles.caption}>
              {open ? "Welcome Preview · Guest Signing Is Open" : "Welcome preview · Signing stays disabled until opened"}
            </Text>
          </Panel>
          <Panel style={styles.panel}>
            <Text accessibilityRole="header" style={styles.title}>
              {open ? "Register Is Open" : "Open the Guest Register"}
            </Text>
            <Text style={styles.body}>
              Opening publishes this event’s welcome, portrait, and remembrance.
              Guest signing stays open until you close it. Online access is
              required.
            </Text>
            <Button
              label={open ? "Close Guest Sign-In" : "Open Guest Sign-In"}
              disabled={readOnly || busy || !saved}
              onPress={() => void mutate(open ? "close" : "open")}
            />
            {!saved ? (
              <Text style={styles.body}>Save your changes first.</Text>
            ) : null}
            {url && draft.registerStatus !== "draft" ? (
              <>
                <View nativeID="register-qr-sign" style={styles.qr}>
                  <QRCode value={url} size={150} />
                </View>
                <View style={styles.actions}>
                  <Button
                    label="Open Tablet Register"
                    variant="secondary"
                    onPress={() => void Linking.openURL(url)}
                  />
                  <Button
                    label="Copy Tablet Link"
                    variant="link"
                    onPress={() =>
                      void Clipboard.setStringAsync(url)
                        .then(() => setNotice("Tablet link copied."))
                        .catch(() =>
                          setError(
                            "The link could not be copied. Open the tablet register instead.",
                          ),
                        )
                    }
                  />
                </View>
                {Platform.OS === "web" ? (
                  <Button
                    label="Download QR Sign"
                    variant="secondary"
                    onPress={() => {
                      try {
                        const svg = document
                          .getElementById("register-qr-sign")
                          ?.querySelector("svg")?.outerHTML;
                        if (!svg) throw new Error();
                        const escape = (value: string) =>
                          value.replace(
                            /[&<>"']/g,
                            (char) =>
                              ({
                                "&": "&amp;",
                                "<": "&lt;",
                                ">": "&gt;",
                                '"': "&quot;",
                                "'": "&#39;",
                              })[char]!,
                          );
                        const html = `<!doctype html><html><meta charset="utf-8"><title>Guest Register QR Sign</title><style>body{font-family:Arial,sans-serif;text-align:center;color:#122f63;padding:50px}svg{width:260px;height:260px;margin:35px}h1{font-size:34px}p{line-height:1.7}</style><h1>In Loving Memory</h1><h2>${escape(data.fullName)}</h2><p>${escape(draft.title)}</p>${svg}<h2>Sign the Guest Register</h2><p>Scan with your phone camera.<br>No account needed.</p><p>wehonor</p></html>`;
                        const link = document.createElement("a");
                        const fileUrl = URL.createObjectURL(
                          new Blob([html], { type: "text/html" }),
                        );
                        link.href = fileUrl;
                        link.download = "guest-register-qr-sign.html";
                        link.click();
                        setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
                        setNotice(
                          "QR sign downloaded. Open the file to print it.",
                        );
                      } catch {
                        setError(
                          "The QR sign could not be downloaded. Please try again.",
                        );
                      }
                    }}
                  />
                ) : null}
                <Text style={styles.body}>
                  Scan this code to open the register on a phone or iPad.
                </Text>
              </>
            ) : null}
          </Panel>
        </>
      ) : (
        <CentralAttendanceOperations
          key={draft.id}
          caseId={personId}
          funeralHomeId={data.funeralHomeId}
          slug={data.slug}
          lovedOneName={data.fullName}
          serviceEvents={[{ id: draft.id, title: draft.title }]}
        />
      )}
    </View>
  );
}
function Alert({ message }: { message: string }) {
  return (
    <View accessibilityRole="alert" style={styles.alert}>
      <Text style={styles.error}>{message}</Text>
    </View>
  );
}
function DateField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled: boolean;
}) {
  if (Platform.OS !== "web")
    return (
      <Field
        label={label}
        value={value}
        onChangeText={onChange}
        editable={!disabled}
        placeholder="YYYY-MM-DDTHH:mm"
        containerStyle={styles.flex}
      />
    );
  return (
    <View style={styles.flex}>
      <Text style={styles.dateLabel}>{label}</Text>
      {createElement("input", {
        type: "datetime-local",
        "aria-label": label,
        value,
        disabled,
        onChange: (e: { target: { value: string } }) =>
          onChange(e.target.value),
        onInput: (e: { target: { value: string } }) => onChange(e.target.value),
        style: {
          boxSizing: "border-box",
          width: "100%",
          minWidth: 0,
          border: `1px solid ${central.border.strong}`,
          borderRadius: 10,
          padding: 14,
          fontFamily: "Manrope, sans-serif",
          fontSize: 16,
          color: central.text.primary,
          background: central.surface.raised,
        },
      })}
    </View>
  );
}
const styles = StyleSheet.create({
  page: {
    width: "100%",
    maxWidth: 1100,
    alignSelf: "center",
    gap: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  headingRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: spacing.md,
  },
  flex: { flex: 1, minWidth: 180 },
  heading: {
    fontFamily: central.typography.label,
    fontSize: 28,
    lineHeight: 36,
    color: central.text.primary,
  },
  title: {
    fontFamily: central.typography.label,
    fontSize: 17,
    lineHeight: 25,
    color: central.text.primary,
  },
  eventName: {
    fontFamily: central.typography.label,
    fontSize: 23,
    lineHeight: 32,
    color: central.text.primary,
  },
  body: {
    fontFamily: central.typography.body,
    fontSize: 14,
    lineHeight: 22,
    color: central.text.secondary,
  },
  caption: {
    fontFamily: central.typography.bodyMedium,
    fontSize: 12,
    lineHeight: 20,
    color: central.text.muted,
  },
  sourceText: {
    fontFamily: central.typography.body,
    fontSize: 13,
    lineHeight: 22,
    color: central.text.secondary,
  },
  actions: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  tabs: { flexDirection: "row", flexWrap: "wrap", gap: spacing.sm },
  panel: { gap: spacing.md },
  eventCard: { gap: spacing.md },
  portrait: { width: 64, height: 76, borderRadius: 10 },
  smallPortrait: { width: 44, height: 52, borderRadius: 6 },
  portraitPlaceholder: {
    width: 64,
    height: 76,
    borderRadius: 10,
    backgroundColor: central.surface.muted,
    alignItems: "center",
    justifyContent: "center",
  },
  websiteRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: central.border.subtle,
  },
  divider: {
    height: 1,
    backgroundColor: central.border.subtle,
    marginVertical: spacing.sm,
  },
  columns: { flexDirection: "row", gap: spacing.md },
  stacked: { flexDirection: "column" },
  dateLabel: {
    fontFamily: central.typography.bodyMedium,
    fontSize: 13,
    lineHeight: 20,
    color: central.text.primary,
    marginBottom: spacing.sm,
  },
  alert: {
    backgroundColor: central.surface.muted,
    borderColor: central.border.strong,
    borderWidth: 1,
    borderRadius: 10,
    padding: spacing.md,
  },
  error: {
    fontFamily: central.typography.body,
    fontSize: 14,
    lineHeight: 22,
    color: central.text.primary,
  },
  notice: {
    fontFamily: central.typography.bodyMedium,
    fontSize: 14,
    lineHeight: 22,
    color: central.action.primary,
  },
  preview: { alignItems: "center", gap: spacing.md, padding: spacing.xl },
  previewPhoto: { width: 160, height: 190, borderRadius: 12 },
  qr: { alignSelf: "center", backgroundColor: "white", padding: spacing.md },
});

function newRegisterService(): RegisterService {
  return { id: `service-${Date.now().toString(36)}`, title: "Memorial Service", localStart: "", localEnd: "", startAt: "", endAt: "", timeZone: "", type: "Memorial Service", venue: "", address: "", registerStatus: "draft", registerStatusLabel: "Draft" };
}
